import { useAuthenticatedUser } from '@ifixit/auth-sdk/client';
import type { User } from '@ifixit/auth-sdk';
import { captureException, SentryError } from '@ifixit/sentry';
import {
   type CartWarning,
   type CountryCode,
   type DisplayableError,
   type LanguageCode,
   type StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { performAddToCart } from './use-add-to-cart';
import { useGetCustomerAccessToken } from '../use-get-customer-access-token';
import { getSyncedAttributes } from '../../helpers/attributes';
import { wasCustomerTokenRejected } from '../../helpers/customer-token-errors';
import {
   clearStoredCartId,
   clearStoredCustomerAccessToken,
   getStoredCartId,
   updateCartId,
} from '../../helpers/storage';
import { getCart, updateCartAttributes } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { useCartKeys } from './use-cart-keys';
import { useTranslations } from '@ifixit/i18n/hooks';
import type { CartMutationType, Cart } from '../../types';
import { toast } from '@ifixit/ui/components/toast';
import { useAppContext } from '@ifixit/app/hooks';

export function useCart() {
   const { data: user } = useAuthenticatedUser();
   const { queryKey } = useCartKeys();
   const getCustomerAccessToken = useGetCustomerAccessToken();
   const {
      client,
      currencyCode: fallbackCurrencyCode,
      country,
      language,
      storeCode,
   } = useShopifyStorefrontClient();
   const isMutating = useIsMutating();
   const { isOnMobileApp } = useAppContext();

   const query = useQuery({
      queryKey,
      enabled: !isMutating,
      refetchOnWindowFocus: 'always', // refetch when switching tabs
      staleTime: 60 * 1000, // 1 minute
      queryFn: async () => {
         const loggedInCartId = user ? getStoredCartId({ user, storeCode }) : null;
         const anonymousCartId = getStoredCartId({ user: null, storeCode });
         const loggedInCart = loggedInCartId
            ? await getCart(client, { cartId: loggedInCartId, country, language })
            : null;
         const anonymousCart = anonymousCartId
            ? await getCart(client, { cartId: anonymousCartId, country, language })
            : null;
         const allUserErrors: DisplayableError[] = [];
         const allValidationErrors: string[] = [];
         const allWarnings: CartWarning[] = [];
         let finalCart = loggedInCart ?? anonymousCart;

         if (anonymousCart && user) {
            let mergedCart = null;
            let mergedCartUser: User | null = user;

            if (loggedInCart) {
               const { cart, userErrors, validationErrors, warnings } = await performAddToCart({
                  cartId: loggedInCart.id,
                  client,
                  country,
                  lines: buildIfixitCart({
                     cart: anonymousCart,
                     fallbackCurrencyCode,
                     userErrors: [],
                     validationErrors: [],
                     warnings: [],
                  }).lineItems,
                  language,
               });
               mergedCart = cart ?? loggedInCart;
               allUserErrors.push(...userErrors);
               allValidationErrors.push(...validationErrors);
               allWarnings.push(...warnings);
            } else {
               const { cart, cartUser, userErrors, validationErrors, warnings } =
                  await updateCartBuyerIdentity({
                     client,
                     cartId: anonymousCart.id,
                     country,
                     language,
                     getCustomerAccessToken,
                     storeCode,
                     user,
                  });
               mergedCart = cart ?? anonymousCart;
               mergedCartUser = cartUser;
               allUserErrors.push(...userErrors);
               allValidationErrors.push(...validationErrors);
               allWarnings.push(...warnings);
            }

            if (mergedCart) {
               updateCartId({
                  cartId: mergedCart.id,
                  storeCode,
                  userId: mergedCartUser?.id ?? null,
               });
            }
            if (mergedCartUser) {
               clearStoredCartId({ user: null, storeCode });
            }
            finalCart = mergedCart;
         }

         const { attributes, needsSync } = getSyncedAttributes({ cart: finalCart, isOnMobileApp });
         if (finalCart && needsSync) {
            const { cart, userErrors, validationErrors, warnings } = await updateCartAttributes(
               client,
               {
                  attributes,
                  cartId: finalCart.id,
                  country,
                  language,
               }
            );
            finalCart = cart ?? null;
            allUserErrors.push(...userErrors);
            allValidationErrors.push(...validationErrors);
            allWarnings.push(...warnings);
         }

         if ((loggedInCartId || anonymousCartId) && finalCart == null) {
            clearStoredCartId({ storeCode, user: user ?? null });
         }

         return buildIfixitCart({
            cart: finalCart,
            fallbackCurrencyCode,
            userErrors: allUserErrors,
            validationErrors: allValidationErrors,
            warnings: allWarnings,
         });
      },
   });
   return query;
}

export async function updateCartBuyerIdentity({
   client,
   cartId,
   country,
   language,
   getCustomerAccessToken,
   storeCode,
   user,
}: {
   client: StorefrontClient;
   cartId: string;
   country: CountryCode;
   language: LanguageCode;
   getCustomerAccessToken: ReturnType<typeof useGetCustomerAccessToken>;
   storeCode: string;
   user: User;
}) {
   const update = (accessToken: string | undefined) =>
      client.updateCartBuyerIdentity({
         cartId,
         country,
         buyerIdentity: { customerAccessToken: accessToken },
         language,
      });

   const stored = await getCustomerAccessToken(user);
   const storedToken = stored?.accessToken;
   let response = await update(storedToken);
   if (wasCustomerTokenRejected(response.cartBuyerIdentityUpdate, storedToken)) {
      clearStoredCustomerAccessToken({ storeCode, user });
      const fresh = await getCustomerAccessToken(user, { previousTokenRejected: true });
      const freshToken = fresh?.accessToken;
      response = await update(freshToken);

      if (wasCustomerTokenRejected(response.cartBuyerIdentityUpdate, freshToken)) {
         captureException(
            new SentryError('Shopify rejected a fresh customer access token', {
               extra: {
                  cartId,
                  storeCode,
                  userErrors: response.cartBuyerIdentityUpdate?.userErrors,
               },
            })
         );
         clearStoredCustomerAccessToken({ storeCode, user });
         return {
            cart: response.cartBuyerIdentityUpdate?.cart ?? null,
            cartUser: null,
            userErrors: [],
            validationErrors: [],
            warnings: response.cartBuyerIdentityUpdate?.warnings ?? [],
         };
      }
   }
   if (!response.cartBuyerIdentityUpdate?.cart) {
      throw new SentryError('Failed to update cart buyer identity', { extra: { cartId, user } });
   }
   return {
      cart: response.cartBuyerIdentityUpdate.cart,
      cartUser: user,
      userErrors: response.cartBuyerIdentityUpdate.userErrors || [],
      validationErrors: [],
      warnings: response.cartBuyerIdentityUpdate.warnings || [],
   };
}

function useGetCartWarningDescription() {
   // https://shopify.dev/docs/api/storefront/2024-10/enums/CartWarningCode
   const t = useTranslations('ErrorHandling');
   return (CartWarning: CartWarning) => {
      switch (CartWarning.code) {
         case 'MERCHANDISE_NOT_ENOUGH_STOCK':
         case 'MERCHANDISE_OUT_OF_STOCK': {
            return CartWarning.message;
         }
         default: {
            return t('tryActionAgain');
         }
      }
   };
}

const GENERIC_CART_ERROR_ID = 'generic-cart-error';

// Ids of the cart toasts currently on screen, so showErrorToast can clear them
// before it replaces them. Each id is removed again when its toast closes.
const firedCartToastIds = new Set<string>();

export function useCartToasts({ title }: { title: string }) {
   const t = useTranslations();
   const getCartWarningDescription = useGetCartWarningDescription();

   const showCartToast = (
      kind: 'error' | 'warning',
      id: string,
      toastTitle: string,
      description?: string
   ) => {
      const forget = () => firedCartToastIds.delete(id);
      firedCartToastIds.add(id);
      toast[kind](toastTitle, {
         id,
         description,
         onAutoClose: forget,
         onDismiss: forget,
      });
   };

   const showCartWarnings = ({
      cart,
      type,
      variables,
   }: {
      cart: Cart;
      type: CartMutationType;
      variables: unknown;
   }) => {
      // Show a single toast for user errors
      if (cart.userErrors && cart.userErrors.length > 0) {
         const msg = `Cart user errors: ${type}`;
         console.error(msg, cart.userErrors);
         captureException(
            new SentryError(msg, {
               extra: {
                  'User Errors': JSON.stringify(cart.userErrors, null, 2),
                  Variables: JSON.stringify(variables, null, 2),
               },
            })
         );
         showCartToast('error', `${type}-user-error`, title, t('ErrorHandling.tryActionAgain'));
      }

      // Show validation error toasts if there are any
      if (cart.validationErrors && cart.validationErrors.length > 0) {
         cart.validationErrors.forEach(error => {
            showCartToast('error', `${type}-validation-error-${error}`, error);
         });
      }

      // Show warnings toasts if there are any
      if (cart.warnings && cart.warnings.length > 0) {
         cart.warnings.forEach(warning => {
            showCartToast(
               'warning',
               `${type}-warning-${warning.message}`,
               title,
               getCartWarningDescription(warning)
            );
         });
      }
   };

   const showErrorToast = () => {
      // Leave the generic id alone: sonner defers a dismiss to a requestAnimationFrame
      // and a create to a setTimeout, so dismissing an id we re-create in the same tick
      // lands the dismiss last and deletes the replacement. Re-creating it is enough,
      // sonner updates a toast that is already on screen in place.
      firedCartToastIds.forEach(id => {
         if (id !== GENERIC_CART_ERROR_ID) toast.dismiss(id);
      });
      firedCartToastIds.clear();
      showCartToast('error', GENERIC_CART_ERROR_ID, title, t('ErrorHandling.tryActionAgain'));
   };

   return { showCartWarnings, showErrorToast };
}
